import React, { ReactNode } from 'react';
import type { TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import useDataTable from 'src/shared/components/data-table/hooks/useDataTable';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import styles from 'src/shared/components/data-table/DataTable.module.scss';
import classNames from 'classnames';

const TableRow = (props: { rowData: TableRowData; rowId: string | number; skipColumns?: number[]; rowSpans?: number[] }) => {
    const {
        columns,
        hiddenColumnIds,
        selectedRowIds,
        onRowClick,
        sortedColumn,
        isSelectable,
        selectableColumnIndex,
        disableRowHover,
        dispatch,
    } = useDataTable();
    const cells = props.rowData;
    const uniqueColumnIndex = columns.findIndex(column => column.isUnique);
    // Use the unique column if available, otherwise fall back to rowId or first column
    const uniqueRowId = (uniqueColumnIndex >= 0 ? cells[uniqueColumnIndex] : props.rowId) as string | number;
    const rowClassName = classNames(styles.row, {
        [styles.rowSelected]: selectedRowIds && selectedRowIds.has(uniqueRowId),
        [styles.rowNoHover]: disableRowHover,
    });

    const handleCheckboxChange = (_checked: boolean) => {
        dispatch({
            type: 'toggle_row_selection',
            payload: uniqueRowId,
        });
    };

    const handleRowClick = (e: React.MouseEvent) => {
        // Don't trigger row click if clicking on the checkbox
        if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
            return;
        }
        onRowClick?.(props.rowData);
    };

    const isEmptyRow = !uniqueRowId || uniqueRowId === '';

    return (
        <tr className={rowClassName} data-row-id={uniqueRowId} onClick={handleRowClick}>
            {isSelectable && selectableColumnIndex === 0 && (
                <td key='select-cell' className={styles.cellCheckbox}>
                    {!isEmptyRow && <Checkbox checked={selectedRowIds.has(uniqueRowId)} onChange={handleCheckboxChange} />}
                </td>
            )}
            {cells?.map((cellData, index) => {
                if (props.skipColumns && props.skipColumns.includes(index)) {
                    return null;
                }
                const currentColumn = columns[index];
                if ((hiddenColumnIds && hiddenColumnIds.has(currentColumn?.columnId)) || !currentColumn) {
                    return null;
                }

                const { renderer, bodyCellClassName } = currentColumn;
                const isSortedColumn = sortedColumn && currentColumn?.columnId === sortedColumn.columnId;

                // skip renderer for coloumns with row span (merged columns)
                const isMergedColumn = props.rowSpans && props.rowSpans[index] && props.rowSpans[index] > 1;
                const shouldApplyRenderer = renderer && !isMergedColumn;

                // A plain JSX value, not a component defined in this render - defining a
                // component inline here would give it a new identity every render, and React
                // treats a changed component type as a different thing entirely: it unmounts
                // and remounts the whole cell (and any state a custom renderer holds) on every
                // re-render of this row, even when rowData is unchanged.
                const cellContents =
                    typeof cellData === 'string' && cellData.startsWith('<') ? (
                        // If cellData starts with '<', assume it's HTML code
                        <td key={index} className={bodyCellClassName} dangerouslySetInnerHTML={{ __html: cellData }} />
                    ) : (
                        <td
                            key={index}
                            className={classNames(bodyCellClassName, {
                                [styles.cellSorted]: isSortedColumn,
                                [styles.mergedCell]: isMergedColumn,
                            })}
                            rowSpan={props.rowSpans ? props.rowSpans[index] : undefined}
                        >
                            {shouldApplyRenderer
                                ? renderer({
                                      rowData: props.rowData,
                                      rowId: props.rowId,
                                      cellData,
                                  })
                                : (cellData as ReactNode)}
                        </td>
                    );

                return (
                    <React.Fragment key={index}>
                        {cellContents}
                        {isSelectable && selectableColumnIndex === index + 1 && (
                            <td key={`select-${index}`} className={styles.cellCheckbox}>
                                {!isEmptyRow && <Checkbox checked={selectedRowIds.has(uniqueRowId)} onChange={handleCheckboxChange} />}
                            </td>
                        )}
                    </React.Fragment>
                );
            })}
        </tr>
    );
};

export default TableRow;
